import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import "@/App.css";
import { HeroPage } from "./views/HeroPage.jsx";
import { LibraryView } from "./views/LibraryView.jsx";
import { AnnualPlanView } from "./views/AnnualPlanView.jsx";
import { ProgramsView } from "./views/ProgramsView.jsx";
import { ContextEngineView } from "./views/ContextEngineView.jsx";
import { QuickCommsView } from "./views/QuickCommsView.jsx";
import { AppHeader } from "./views/AppHeader.jsx";
import DashboardView from "./views/DashboardView.jsx";
import AdminView from "./views/AdminView.jsx";
import { SitesManager } from "./views/SitesManager.jsx";
import PricingPage from "./views/PricingPage.jsx";
import PendingActivationView from "./views/PendingActivationView.jsx";
import LumaPanel from "./views/LumaPanel.jsx";
import HelpView from "./views/HelpView.jsx";
import WelcomeTour from "./views/WelcomeTour.jsx";
import { useAuth } from "./hooks/useAuth";
import { useSites, mergeSiteProfile } from "./hooks/useSites";
import {
  API, INDUSTRY_CODES, logDev, safeStorage,
} from "./lib/beaconUtils";

const EMPTY_FORM = {
  site_name: "",
  industry: "Construction",
  jurisdictions: ["VIC"],
  free_text_context: "",
  real_examples: "",
  tone: "conversational",
  audience: "field",
  company_footer: "",
  company_logo_b64: null,
  brand_palette: null,
};

function App() {
  const [topics, setTopics] = useState([]);
  const [health, setHealth] = useState(null);
  const [bootError, setBootError] = useState(null);

  // ---- Context Engine form + applied snapshot ----
  // No auto-apply — the user must hit Apply on the Context Engine before
  // Quick Comms unlocks. This snapshotting is deliberate so generation
  // never reads half-edited form state.
  const [form, setForm] = useState(EMPTY_FORM);
  const [appliedProfile, setAppliedProfile] = useState(null);
  const [appliedAt, setAppliedAt] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  // ---- Auth (Emergent-managed Google SSO; httpOnly cookie sessions) ----
  const {
    authUser, authChecking, authError,
    signInWithGoogle, signOut,
  } = useAuth({
    onSignedOut: () => {
      // Wipe in-memory profile so nothing bleeds across sessions.
      setForm(EMPTY_FORM);
      setAppliedProfile(null);
      setAppliedAt(null);
      setSavedAt(null);
      safeStorage.remove("beacon-active-site");
    },
  });

  // ---- Multi-site annual plans ----
  const {
    sites, tier, maxSites, activeSite, activeSiteId, planId,
    selectSite, addSite, updateSite, deleteSite,
  } = useSites(authUser);

  // The profile driving plan/dashboard/pack generation — company context
  // merged with the active site's notes.
  const siteProfile = useMemo(
    () => mergeSiteProfile(appliedProfile, activeSite),
    [appliedProfile, activeSite],
  );

  // First-time users land on the Context Engine. Returning users (who
  // have applied context at least once on this browser) land on the
  // Dashboard cockpit.
  const [activeView, setActiveView] = useState(() => {
    if (typeof window === "undefined") return "context";
    return window.localStorage.getItem("beacon-context-applied") === "true"
      ? "dashboard"
      : "context";
  });
  const [theme, setTheme] = useState(
    () => (typeof window !== "undefined" &&
      window.localStorage.getItem("beacon-theme")) || "dark"
  );

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    safeStorage.set("beacon-theme", theme);
  }, [theme]);

  useEffect(() => {
    const openHelp = () => setActiveView("help");
    window.addEventListener("beacon-open-help", openHelp);
    return () => window.removeEventListener("beacon-open-help", openHelp);
  }, []);

  // Welcome tour — auto once for first-time activated users, replayable
  // from the Help page via the "beacon-start-tour" event.
  const [showTour, setShowTour] = useState(false);
  useEffect(() => {
    if (!authUser || authUser.activated === false) return;
    if (!window.localStorage.getItem("beacon-tour-done")) setShowTour(true);
  }, [authUser]);
  useEffect(() => {
    const start = () => setShowTour(true);
    window.addEventListener("beacon-start-tour", start);
    return () => window.removeEventListener("beacon-start-tour", start);
  }, []);

  // Pull a saved brand profile for the just-authenticated user and hydrate
  // the Context Engine form + applied snapshot. Never runs for guests.
  const loadSavedProfile = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/profile`, {
        withCredentials: true,
        validateStatus: (s) => s < 500,
      });
      if (r.status !== 200) return;
      const saved = r.data?.brand_profile || {};
      if (!saved || Object.keys(saved).length === 0) return;
      const next = {
        site_name: saved.site_name ?? "",
        industry: saved.industry ?? "Construction",
        jurisdictions: saved.jurisdictions ?? ["VIC"],
        free_text_context: saved.free_text_context ?? "",
        real_examples: saved.real_examples ?? "",
        tone: saved.tone ?? "conversational",
        audience: saved.audience ?? "field",
        company_footer: saved.company_footer ?? "",
        company_logo_b64: saved.company_logo_b64 ?? null,
        brand_palette: saved.brand_palette ?? null,
      };
      setForm(next);
      setAppliedProfile(next);
      setAppliedAt(new Date());
      // A signed-in user with saved context shouldn't briefly see the
      // Context Engine on boot — land them on the Dashboard cockpit.
      setActiveView("dashboard");
      safeStorage.set("beacon-context-applied", "true");
    } catch (err) {
      logDev("Failed to load saved profile", err);
    }
  }, []);

  // Whenever the user transitions from anonymous → signed-in, pull their
  // saved brand profile from the server. Guests skip this entirely.
  useEffect(() => {
    if (authUser?.user_id) {
      loadSavedProfile();
    }
  }, [authUser?.user_id, loadSavedProfile]);

  const profileIsDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(appliedProfile),
    [form, appliedProfile],
  );

  const applyContext = async () => {
    setAppliedProfile(form);
    setAppliedAt(new Date());
    safeStorage.set("beacon-context-applied", "true");
    // Signed-in users get their context lifted server-side on every Apply,
    // so the same site follows them across browsers/devices. Guests keep
    // going against localStorage only.
    if (authUser?.user_id) {
      setSavingProfile(true);
      try {
        await axios.put(`${API}/profile`, form, {
          withCredentials: true,
        });
        setSavedAt(new Date());
      } catch (err) {
        logDev("Failed to save profile server-side", err);
      } finally {
        setSavingProfile(false);
      }
    }
    // After applying for the first time, hop straight into Quick Comms so
    // the user feels the snapshot took effect.
    setActiveView("quick");
  };

  // ---- Pack workspace state (shared between Quick Comms + Library) ----
  const [currentPack, setCurrentPack] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  // Topic handed over from the Annual Plan's "Generate pack for this
  // week" — may be a plan-built (ad-hoc) topic not in the static library.
  const [jumpTopic, setJumpTopic] = useState(null);
  const [lumaReview, setLumaReview] = useState(null);
  const [rebuildRequest, setRebuildRequest] = useState(null);

  useEffect(() => {
    axios.get(`${API}/health`)
      .then((h) => setHealth(h.data))
      .catch((e) => setBootError(`Backend unreachable: ${e.message}`));
  }, []);

  // Re-fetch topics whenever the APPLIED industry changes — never the
  // in-flight draft. Draft edits stay local to the Context Engine until
  // the user hits Apply.
  const industryCode = appliedProfile && INDUSTRY_CODES[appliedProfile.industry];
  useEffect(() => {
    if (!industryCode) return undefined;
    let cancelled = false;
    axios.get(`${API}/topics`, { params: { industry: industryCode } })
      .then((r) => { if (!cancelled) setTopics(r.data.topics); })
      .catch((e) => { if (!cancelled) setBootError(`Topics fetch failed: ${e.message}`); });
    return () => { cancelled = true; };
  }, [industryCode]);

  const toggleJurisdiction = (j) => {
    setForm((prev) => {
      const has = prev.jurisdictions.includes(j);
      return {
        ...prev,
        jurisdictions: has
          ? prev.jurisdictions.filter((x) => x !== j)
          : [...prev.jurisdictions, j],
      };
    });
  };

  const profileValid =
    form.site_name.trim() && form.industry && form.jurisdictions.length > 0;

  // Public pricing page — renders for anyone, signed in or not.
  if (typeof window !== "undefined" && window.location.pathname === "/pricing") {
    return <PricingPage onSignInGoogle={signInWithGoogle} />;
  }

  // Hold the UI back while /auth/me is in-flight OR the OAuth callback is
  // being exchanged. Otherwise a signed-in user briefly sees the hero.
  if (authChecking) {
    return (
      <div className="beacon-auth-boot" data-testid="auth-checking">
        <span className="beacon-spinner beacon-spinner-dark" />
      </div>
    );
  }

  // Closed pilot: no guest mode. Everyone signs in with Google…
  if (!authUser) {
    return <HeroPage
      onSignInGoogle={signInWithGoogle}
      authError={authError}
    />;
  }

  // …and only admin-activated accounts get past this gate.
  if (authUser.activated === false) {
    return <PendingActivationView authUser={authUser} onSignOut={signOut} />;
  }

  return (
    <div className="beacon-shell" data-testid="beacon-shell">
      <AppHeader
        activeView={activeView}
        setActiveView={setActiveView}
        theme={theme}
        setTheme={setTheme}
        health={health}
        authUser={authUser}
        onSignOut={signOut}
        onSignIn={signInWithGoogle}
      />

      <main className="beacon-main">
        {bootError && (
          <div className="beacon-alert" data-testid="boot-error">
            {bootError}
          </div>
        )}
        {health?.stub_mode && (
          <div className="beacon-stub-banner" data-testid="stub-banner">
            <strong>Stub mode active.</strong> Live Claude needs{" "}
            <code>BEACON_STUB_MODE=false</code> and an{" "}
            <code>EMERGENT_LLM_KEY</code> in backend <code>.env</code>.
          </div>
        )}

        {activeView === "dashboard" && (
          <DashboardView
            appliedProfile={siteProfile}
            planId={planId}
            sites={sites}
            activeSiteId={activeSiteId}
            onSelectSite={selectSite}
            onGenerateTopic={(topic) => {
              setJumpTopic(topic);
              setActiveView("quick");
            }}
            onOpenPack={(pack) => {
              setCurrentPack(pack);
              setActiveView("quick");
            }}
            onGoPlan={() => setActiveView("plan")}
            onGoQuick={() => setActiveView("quick")}
            onGoLibrary={() => setActiveView("library")}
          />
        )}

        {activeView === "admin" && authUser?.role === "admin" && (
          <AdminView authUser={authUser} />
        )}

        {activeView === "help" && (
          <HelpView onAskLuma={() => {
            window.dispatchEvent(new Event("beacon-open-luma"));
          }} />
        )}

        {activeView === "context" && (
          <>
            <ContextEngineView
              form={form}
              setForm={setForm}
              toggleJurisdiction={toggleJurisdiction}
              profileIsDirty={profileIsDirty}
              appliedAt={appliedAt}
              applyContext={applyContext}
              profileValid={profileValid}
              authUser={authUser}
              savingProfile={savingProfile}
              savedAt={savedAt}
            />
            {authUser?.user_id && sites.length > 0 && (
              <SitesManager
                sites={sites}
                tier={tier}
                maxSites={maxSites}
                activeSiteId={activeSiteId}
                onSelectSite={selectSite}
                addSite={addSite}
                updateSite={updateSite}
                deleteSite={deleteSite}
              />
            )}
          </>
        )}

        {activeView === "quick" && (
          <QuickCommsView
            form={form}
            appliedProfile={siteProfile}
            profileIsDirty={profileIsDirty}
            appliedAt={appliedAt}
            topics={topics}
            profileValid={profileValid}
            generating={generating}
            setGenerating={setGenerating}
            setGenerationError={setGenerationError}
            generationError={generationError}
            setCurrentPack={setCurrentPack}
            currentPack={currentPack}
            jumpTopic={jumpTopic}
            clearJumpTopic={() => setJumpTopic(null)}
            rebuildRequest={rebuildRequest}
            clearRebuildRequest={() => setRebuildRequest(null)}
            onEditContext={() => setActiveView("context")}
          />
        )}

        {activeView === "plan" && (
          <AnnualPlanView
            topics={topics}
            industryCode={industryCode}
            siteName={activeSite?.name || appliedProfile?.site_name}
            companyFooter={appliedProfile?.company_footer}
            appliedProfile={siteProfile}
            planId={planId}
            sites={sites}
            activeSiteId={activeSiteId}
            onSelectSite={selectSite}
            onJumpToGenerate={(topic) => {
              setJumpTopic(topic);
              setActiveView("quick");
            }}
          />
        )}

        {activeView === "programs" && (
          <ProgramsView
            onForked={() => {
              setActiveView("plan");
            }}
          />
        )}

        {activeView === "library" && (
          <LibraryView
            onOpenPack={(pack) => {
              setCurrentPack(pack);
              setActiveView("quick");
            }}
            onReviewPack={setLumaReview}
          />
        )}
      </main>

      <footer className="beacon-footer">
        <span>Beacon · H&amp;S comms, calibrated.</span>
        <span>v0.4 · Phase 1 Block 4</span>
      </footer>

      <LumaPanel activeView={activeView} planId={planId} profile={siteProfile}
        reviewRequest={lumaReview} onReviewHandled={() => setLumaReview(null)}
        onRebuildBrief={(req) => {
          setRebuildRequest(req);
          setCurrentPack(null);
          setGenerationError(null);
          setActiveView("quick");
        }} />

      {showTour && <WelcomeTour onClose={() => setShowTour(false)} />}
    </div>
  );
}

export default App;
